import { ContentService } from '@blk/cwp-ng-framework';
import { AbstractRenderer, Alignment, BusinessComponent, BusinessComponentOptions, CellPanel, ColumnPanel, PageBreak, RowPanel, StaticParagraphComponent } from '@blk/simba';
import { BenchmarkOption, ViewCategory } from '@ddt-web/config';
import { InjectConfig, SharedConfigKey } from '@ddt-web/config-layer';
import { CellUtil, ComponentData, Expression, PortfolioDataUtil, TabData, TabUtil } from '@ddt-web/data-service';
import { ReportUtils } from '@ddt-web/pdf-utils';
import * as get from 'lodash/get';
import * as isNil from 'lodash/isNil';

import { HeadingComponent, HeadingLevel } from '../../heading/heading-component';
import { HeadingWithDateComponent } from '../../heading/heading-with-date-component';
import { PdfUtils } from '../../helpers';
import { TableUtils } from '../../helpers/utils/table-utils';
import { PerformanceTableUtil } from '../util/performance-table.util';
import { HistoricalReturnsConfig } from './historical-returns.config';

export class HistoricalReturns extends BusinessComponent {
	@InjectConfig(SharedConfigKey.EnableCustomFee) private readonly isCustomFeeEnabled: boolean = false;

	// custom logic for the historical table when one of the portfolio/model in the analysis is set as benchmark
	// this is needed as the table has too many columns and custom logic for column widths and custom net performance padding is needed
	private readonly TABLE_CUSTOM_PROPORTIONS_WHEN_BENCHMARK_SET: number[] = [15, 12, 10.43, 10.43, 9.43, 10.93, 10.93, 10.43, 10.42];
	private readonly TABLE_CUSTOM_PROPORTIONS_WHEN_BENCHMARK_SET_SS: number[] = [18, 15, 13.4, 13.4, 13.4, 13.4, 13.4];

	private grossReturnsTable: TabData;
	private netCustomReturnsTable: TabData;

	constructor(
		name: string,
		public dataComponentId: string,
		public config?: HistoricalReturnsConfig,
		public selectedBenchmarkForCaptureRatio?: string,
		public shouldStartFromNewPage: boolean = false,
		public customAdvisoryFee: number = null,
		public visible: boolean = true,
		public viewCategory?: ViewCategory
	) {
		super(name, 'BusinessComponent', BusinessComponentOptions.merge({ visible: visible, componentId: dataComponentId }));
	}

	public isComponentDataVisible(componentData: ComponentData): boolean {
		const isComponentDataSuccessful = componentData && componentData.success;
		if (isComponentDataSuccessful) {
			this.grossReturnsTable = componentData.getTabById('historical.returns.total');
			this.netCustomReturnsTable = componentData.getTabById('historical.net.custom.returns.total');

			const isHistGrossReturnsValid = TabUtil.isTabValid(this.grossReturnsTable);
			const isHistNetCustomReturnsValid = CellUtil.isValidCellData(this.netCustomReturnsTable) && (this.netCustomReturnsTable?.displayConfig?.visible ?? false);

			// if custom fee is enabled and set, set validity based on both net and gross, otherwise only on gross
			const isHistoricalPerformanceValid = this.isCustomFeeEnabled && this.customAdvisoryFee ?
				isHistGrossReturnsValid && isHistNetCustomReturnsValid : isHistGrossReturnsValid;

			return isHistoricalPerformanceValid;
		} else {
			return false;
		}
	}

	public buildComponent(componentData: ComponentData, contentSvc: ContentService): AbstractRenderer<SVGGElement>[] {
		const panels: AbstractRenderer<SVGGElement>[] = [];

		if (this.isComponentDataVisible(componentData)) {

			if (this.shouldStartFromNewPage) {
				panels.push(new PageBreak());
			}
			const dcrKeyPrefix = ReportUtils.createDcrKeyPrefixForPdf(this.name);
			const asOfDate = get(componentData.getTabById('hypo-perf-as-of-date'), 'tableData[0].cells[0][0].formattedValue');
			const historicalGeneralPanels = this.buildHistoricalGeneralPanels(dcrKeyPrefix, asOfDate);
			const benchmarkName = PortfolioDataUtil.getBenchmarkPortfolioName(componentData.portfolioData);

			if (benchmarkName === '') {
				// If there is no benchmark, show SNP500 
				this.selectedBenchmarkForCaptureRatio = BenchmarkOption.SNP500;
			}

			let historicalTablePanels;
			if (this.grossReturnsTable && this.netCustomReturnsTable) {
				let grossTabDataWithValidColumns = this.grossReturnsTable;
				let netTabDataWithValidColumns = this.netCustomReturnsTable;
				let columnProportions = this.TABLE_CUSTOM_PROPORTIONS_WHEN_BENCHMARK_SET;
				if (!ReportUtils.isPsReport(this.viewCategory) && this.selectedBenchmarkForCaptureRatio) {
					grossTabDataWithValidColumns = PdfUtils.getTabWithValidColumns(this.grossReturnsTable,
						this.selectedBenchmarkForCaptureRatio, BenchmarkOption.BENCHMARK.toLowerCase(), BenchmarkOption.SNP500.toLowerCase());
					netTabDataWithValidColumns = PdfUtils.getTabWithValidColumns(this.netCustomReturnsTable,
						this.selectedBenchmarkForCaptureRatio, BenchmarkOption.BENCHMARK.toLowerCase(), BenchmarkOption.SNP500.toLowerCase());
					columnProportions = this.TABLE_CUSTOM_PROPORTIONS_WHEN_BENCHMARK_SET_SS;
				}

				historicalTablePanels = PerformanceTableUtil.buildCustomFeePerformanceTable(
					[grossTabDataWithValidColumns, netTabDataWithValidColumns],
					dcrKeyPrefix,
					false,
					this.customAdvisoryFee,
					contentSvc,
					!!benchmarkName && ReportUtils.isPsReport(this.viewCategory) ? columnProportions : null,
					null,
					null
				);
			} else {
				historicalTablePanels = this.buildGrossHistoricalTablePanelItems();
			}

			panels.push(
				new ColumnPanel(
					'Historical Portfolio Performance Section',
					[...historicalGeneralPanels, ...historicalTablePanels],
					{},
					Alignment.Horizontal.Left
				)
			);
		}
		return panels;
	}

	private buildHistoricalGeneralPanels(dcrKeyPrefix: string, asOfDate: string): AbstractRenderer<SVGGElement>[] {
		const startDate = CellUtil.findByExpression(this.grossReturnsTable, [new Expression('startDate', 'tableData[0].cells[0]', 'formattedValue')]);
		const endDate = CellUtil.findByExpression(this.grossReturnsTable, [new Expression('endDate', 'tableData[0].cells[0]', 'formattedValue')]);
		const panels: AbstractRenderer<SVGGElement>[] = [];

		panels.push(
			new HeadingWithDateComponent().buildHeader('Historical Portfolio Performance Header', 'PERFORMANCE_H2', this.name, asOfDate)
		);
		if (!isNil(startDate) && !isNil(endDate)) {
			const commonPeriodDateRange = `${startDate} - ${endDate}`;
			panels.push(
				new HeadingComponent('PDF_HISTORICAL_PERF_H3', get(this.grossReturnsTable, 'config.title.label', 'COMMON PERIOD').toString(), HeadingLevel.H3),
				new StaticParagraphComponent(dcrKeyPrefix + 'HISTORICAL_PERF_DATE_RANGE', '{0}', { 0: commonPeriodDateRange }, { paddingBottom: 2 }),
				new StaticParagraphComponent(
					'PDF_HISTORICAL_PERFORMANCE_COMMON_PERIOD_MESSAGE',
					'The historical period for which returns are available for all holdings. The common period begins at the inception date of the newest funds or share class.'
				)
			);
		}
		return panels;
	}

	private buildGrossHistoricalTablePanelItems(): Array<AbstractRenderer<SVGGElement>> {
		let tableHeaderBoxModelOverride;
		let tableBodyBoxModelOverride;

		if (get(this.config, 'tableHeaderBoxModel')) {
			tableHeaderBoxModelOverride = this.config.tableHeaderBoxModel;
		}

		if (get(this.config, 'tableBodyBoxModel')) {
			tableBodyBoxModelOverride = this.config.tableBodyBoxModel;
		}

		const tabDataWithValidColumns = this.selectedBenchmarkForCaptureRatio
			? PdfUtils.getTabWithValidColumns(this.grossReturnsTable, this.selectedBenchmarkForCaptureRatio, BenchmarkOption.BENCHMARK.toLowerCase(), BenchmarkOption.SNP500.toLowerCase())
			: this.grossReturnsTable;
		const grossTableHeaderRow = TableUtils.getHeaderRowForGridFormatOfData(tabDataWithValidColumns, tableHeaderBoxModelOverride);
		const grossTableRows = TableUtils.getRowsForGridFormatOfData(tabDataWithValidColumns, tableBodyBoxModelOverride);

		const grossBodyRow: RowPanel = new RowPanel(
			'Historical Gross Returns Table Body Row',
			[new CellPanel(grossTableRows, { proportion: 100 }, Alignment.Horizontal.Left)], { paddingBottom: 8 }
		);
		const grossOnlyHistoricalReturnTablePanels = [grossTableHeaderRow, grossBodyRow];

		return grossOnlyHistoricalReturnTablePanels;
	}
}
